<template>
  <div>
    <AAlert
      v-if="errorMessage"
      type="error"
      :message="errorMessage"
      banner
      closable
      @close="errorMessage = null"
    />

    <!-- filter -->
    <ARow :gutter="[8, 8]">
      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterDistributor
          style="width:100%"
          :is-tso="hasRoles([ROLE_TSO])"
          v-model:value="state.params.distributor"
        />
      </ACol>

      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterToko
          style="width:100%"
          mode="multiple"
          v-model:value="state.params.toko"
          v-model:vendor_id="state.vendor_id"
        />
      </ACol>

      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterSalesmanSupervisor
          style="width:100%"
          v-model:value="state.params.salesman"
          :vendor_id="state.vendor_id"
          :useram_id="state.useram_id"
        />
      </ACol>

      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterOpco
          style="width:100%"
          v-model:value="state.params.opco"
          placeholder="Pilih AP"
        />
      </ACol>
    </ARow>

    <ARow :gutter="[8, 8]" style="margin-top: 8px;">
      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterProvince
          style="width:100%"
          v-model:value="state.params.provinsi"
          v-model:region="state.params.region"
        />
      </ACol>

      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterArea
          style="width:100%"
          v-model:value="state.params.area"
          v-model:provinsi="state.params.provinsi"
          v-model:region="state.params.region"
        />
      </ACol>

      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterKabupatenKota
          style="width:100%"
          v-model:value="state.params.kabupaten"
          v-model:region="state.params.region"
          v-model:area="state.params.area"
          v-model:provinsi="state.params.provinsi"
        />
      </ACol>

      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <a-select
          v-model:value="state.params.is_followup"
          placeholder="Pilih Status Follow Up"
          style="width: 100%"
          allow-clear>
          <a-select-option value="Belum">Belum</a-select-option>
          <a-select-option value="Sudah">Sudah</a-select-option>
        </a-select>
      </ACol>
    </ARow>

    <ARow :gutter="[8, 8]" style="margin-top: 8px;">
      <ACol :span="24">
        <AButton
          type="primary"
          @click="fetchDataList"
          :loading="state.isFetching"
        >
          <span v-if="!state.isFetching" class="fa fa-search"/>
          <span v-else>Memuat Data ...</span>
        </AButton>
      </ACol>
    </ARow>

    <!-- export -->
    <div class="row justify-content-end mt-4">
      <div class="col-lg-6"/>
      <div class="col-lg-6 text-right">
        <ASpace>
          <ATooltip title="Download excel">
            <DownloadExcel
              :url="state.endpoint.current"
              :params="queryParams()"
              namefile="Tracking-FollowUp-Toko"
              @errors="errorMessage"
            />
          </ATooltip>
        </ASpace>
      </div>
    </div>

    <!-- table -->
    <div class="table-responsive text-nowrap mt-4">
      <MdTable
        :columns="state.columns"
        :data-source="state.data"
        row-key="key"
        size="small"
        :row-class-name="(record, index) =>
          index % 2 === 1 ? 'table-striped' : null"
        :pagination="{
          showSizeChanger: true,
          pageSizeOptions: ['5','10','20','30','50','75'],
          showTotal: (total, range) =>
            `Menampilkan ${range[0]}-${range[1]} Hasil ${total}`,
          total: state.meta.total,
          pageSize: state.meta.per_page,
          current: state.meta.page,
        }"
        @change="handleTableChange"
        :loading="state.isFetching"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, onMounted, computed } from "vue"
import apiClient from "@/services/axios"
import localStorage from "store"

import FilterSalesmanSupervisor from "@/components/filter/FilterSalesmanSupervisor"
import FilterToko               from "@/components/filter/FilterToko"
import FilterDistributor        from "@/components/filter/FilterDistributor"
import FilterOpco               from "@/components/filter/FilterOpco"
import FilterProvince           from "@/components/filter/FilterProvince"
import FilterArea               from "@/components/filter/FilterArea"
import FilterKabupatenKota      from "@/components/filter/FilterKabupatenKota"
import DownloadExcel            from "@/components/Molecules/DownloadExcel"

import {
  hasRoles,
  ROLE_ADMIN_BK,
  ROLE_TSO,
  ROLE_DISTRIBUTOR,
} from "@/helpers"

import {
  updateUrlWithQuery,
  extractQueryParams,
} from "@/helpers/queryparams"

export default defineComponent({
  components: {
    DownloadExcel,
    FilterSalesmanSupervisor,
    FilterToko,
    FilterDistributor,
    FilterOpco,
    FilterProvince,
    FilterArea,
    FilterKabupatenKota,
  },

  setup() {
    const errorMessage = ref(null)
    const user = computed(() => localStorage.get("profile"))

    const state = reactive({
      columns: [
        {
          title: "Nomor TPD",
          dataIndex: "no_support",
          width: 160,
          customHeaderCell: () => ({ style: "white-space: nowrap" }),
          customCell: () => ({ style: 'white-space: nowrap' }),
          customRender: ({ text }) => text ?? "-",
        },
        {
          title: "Tanggal",
          dataIndex: "tanggal",
          width: 120,
          customHeaderCell: () => ({ style: "white-space: nowrap" }),
          customRender: ({ text }) => text ?? "-",
        },
        {
          title: "Kode Toko",
          dataIndex: "customer_code",
          width: 120,
          customHeaderCell: () => ({ style: "white-space: nowrap" }),
          customRender: ({ text }) => text ?? "-",
        },
        {
          title: "Nama Toko",
          dataIndex: "customer_name",
          width: 200,
          customHeaderCell: () => ({ style: "white-space: nowrap" }),
          customRender: ({ text }) => text ?? "-",
        },
        {
          title: "Role",
          dataIndex: "role",
          width: 120,
          customHeaderCell: () => ({ style: "white-space: nowrap" }),
          customRender: ({ text }) => text ?? "-",
        },
        {
          title: "ID Salesman / SPV",
          dataIndex: "user_id",
          width: 150,
          customHeaderCell: () => ({ style: "white-space: nowrap" }),
          customRender: ({ text }) => text ?? "-",
        },
        {
          title: "Nama Salesman / SPV",
          dataIndex: "user_name",
          width: 180,
          customHeaderCell: () => ({ style: "white-space: nowrap" }),
          customRender: ({ text }) => text ?? "-",
        },
        {
          title: "Tanggal Visit",
          dataIndex: "visit_date",
          width: 140,
          customHeaderCell: () => ({ style: "white-space: nowrap" }),
          customRender: ({ text }) => text ?? "-",
        },
        {
          title: "Tanggal Follow Up",
          dataIndex: "followup_date",
          width: 160,
          customHeaderCell: () => ({ style: "white-space: nowrap" }),
          customRender: ({ text }) => text ?? "-",
        },
        {
          title: "Apakah Sudah di Follow Up",
          dataIndex: "is_followup",
          width: 200,
          customHeaderCell: () => ({ style: "white-space: nowrap" }),
          customRender: ({ text }) => text ?? "-",
        },
      ],

      data: [],

      endpoint: {
        current: "/api/report-ats/tracking-followup-toko",
      },

      meta: {
        per_page: 10,
        page: 1,
        total: 0,
      },

      isFetching: false,

      params: extractQueryParams({
        distributor:  [],
        toko:         [],
        salesman:     [],
        opco:         [],
        provinsi:     [],
        area:         [],
        kabupaten:    [],
        region:       [],
        is_followup:  null,
        page:         1,
        "per-page":   10,
      }),

      useram_id: null,
      vendor_id: null,
    })

    const fetchDataList = () => {
      state.isFetching = true

      apiClient
        .get(state.endpoint.current, { params: queryParams() })
        .then(({ data }) => {
          const { items, _meta } = data

          state.data = items.map((item, index) => ({
            ...item,
            key: item.toko_id ?? index,
          }))

          state.meta.page     = _meta.currentPage
          state.meta.per_page = _meta.perPage
          state.meta.total    = _meta.totalCount
        })
        .catch((error) => {
          errorMessage.value =
            error?.response?.data?.message ?? "Terjadi kesalahan saat memuat data."
        })
        .finally(() => {
          state.isFetching = false
        })
    }

    const handleTableChange = (page) => {
      state.params.page        = page.current
      state.params["per-page"] = page.pageSize
      fetchDataList()
    }

    const queryParams = () => updateUrlWithQuery(state.params)

    onMounted(() => {
      fetchDataList()

      if (hasRoles([ROLE_TSO])) {
        state.useram_id = user.value?.id
      }

      if (hasRoles([ROLE_DISTRIBUTOR])) {
        state.vendor_id = user.value?.vendor_id
      }
    })

    return {
      state,
      errorMessage,
      fetchDataList,
      handleTableChange,
      queryParams,

      hasRoles,
      ROLE_ADMIN_BK,
      ROLE_TSO,
      ROLE_DISTRIBUTOR,
    }
  },
})
</script>
